import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { Check, X, Sparkles, RotateCcw, Share2, Download, Image as ImageIcon, ArrowLeft, Users } from "lucide-react";
import api, { imageUrl } from "@/lib/api";
import { useObjectUrl } from "@/hooks/useObjectUrl";
import { prepareImage, PHOTO_ERROR_MSG } from "@/lib/image";
import { VerdictCopyService } from "@/lib/verdicts";
import UploadZone from "@/components/UploadZone";
import AnalyzingSequence from "@/components/AnalyzingSequence";
import { MoireSymbol } from "@/components/MoireLogo";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const OCCASIONS = ["Dinner", "Wedding", "Party", "Graduation", "Night Out", "Work Event", "Festival", "Casual", "Holiday", "Other"];
const GROUP_LABEL = { tops: "top", bottoms: "bottom", dresses: "dress", outerwear: "layer", shoes: "shoes" };

function lookSummary(look) {
  const parts = look.garments.filter((g) => ["dresses", "tops", "bottoms", "shoes"].includes(g.group)).map((g) => g.name);
  const acc = look.accessories.map((a) => a.name);
  return [...parts, ...acc].join(" · ") || "Outfit partly hidden";
}

export default function GroupCheck({ onBack, user }) {
  const [step, setStep] = useState("upload"); // upload | analyzing | confirm | evaluating | result
  const [file, setFile] = useState(null);
  const { url: preview, setFromFile: setPreviewFile, clear: clearPreview } = useObjectUrl();
  const [detected, setDetected] = useState(null);
  const [checkId, setCheckId] = useState(null);
  const [imageUrlRemote, setImageUrlRemote] = useState(null);
  const [occasion, setOccasion] = useState(null);
  const [result, setResult] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [includePhoto, setIncludePhoto] = useState(false);
  const cardRef = React.useRef(null);

  const verdict = useMemo(() => (result ? VerdictCopyService.pickGroup(result.group_score) : null), [result]);
  const shareText = result
    ? `MOIRÉ gave our group look a ${Number(result.group_score).toFixed(1)}/10. ${verdict?.signature || verdict?.reaction || ""}`.trim()
    : "";

  const onFile = async (f) => {
    try {
      const prepared = await prepareImage(f, { maxDim: 2000 }); // group photos keep more detail
      setFile(prepared); setPreviewFile(prepared); analyze(prepared);
    } catch { toast.error(PHOTO_ERROR_MSG); }
  };

  const analyze = async (f) => {
    setStep("analyzing");
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/outfit-check/group/analyze", form);
      if (data.status !== "ok") { toast.error(data.message || "Couldn't read that photo."); setStep("upload"); return; }
      setDetected(data); setCheckId(data.check_id); setImageUrlRemote(data.image_url);
      setStep("confirm");
    } catch { toast.error(PHOTO_ERROR_MSG); setStep("upload"); }
  };

  const retake = () => { setFile(null); clearPreview(); setDetected(null); setResult(null); setStep("upload"); };

  const evaluate = async () => {
    setStep("evaluating");
    try {
      const { data } = await api.post("/outfit-check/group/evaluate", { looks: detected.looks, occasion, check_id: checkId });
      setResult(data); setStep("result");
    } catch (e) {
      toast.error(e?.response?.status === 422 ? "The outfits weren't clear enough to score. Try a closer photo." : "Couldn't score the group. Try again.");
      setStep("confirm");
    }
  };

  const deletePhoto = async () => {
    if (checkId && user) { try { await api.delete(`/outfit-check/photo/${checkId}`); } catch { /* noop */ } }
    setImageUrlRemote(null); setIncludePhoto(false);
    toast.success("Group photo deleted.");
  };

  const doShare = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, backgroundColor: "#0D0D0D" });
      const blob = await (await fetch(dataUrl)).blob();
      const fileImg = new File([blob], "moire-group.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [fileImg] })) {
        await navigator.share({ files: [fileImg], text: shareText, title: "MOIRÉ" });
      } else if (navigator.share) {
        await navigator.share({ text: `${shareText}\nhttps://moire.live`, title: "MOIRÉ" });
      } else {
        const a = document.createElement("a"); a.href = dataUrl; a.download = "moire-group.png"; a.click();
        toast.success("Group card downloaded.");
      }
    } catch { toast.error("Couldn't create the card."); }
  };
  const downloadCard = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, backgroundColor: "#0D0D0D" });
      const a = document.createElement("a"); a.href = dataUrl; a.download = "moire-group.png"; a.click();
      toast.success("Group card downloaded.");
    } catch { toast.error("Couldn't create the card."); }
  };

  return (
    <div className="max-w-3xl mx-auto px-1">
      <button onClick={onBack} data-testid="group-back" className="flex items-center gap-1.5 font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] hover:text-[#C8A97E] transition-colors mb-6">
        <ArrowLeft size={13} /> Solo or Group</button>

      {step === "upload" && (
        <div className="text-center">
          <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">Group Check</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#F5F2EA] mt-2">How are we looking?</h1>
          <p className="font-sans text-sm text-[#8A8170] mt-3">One photo. The whole crew. Let MOIRÉ have a look.</p>
          <div className="mt-8">
            <UploadZone testid="group" label="Snap the crew" preview={preview} onFile={onFile} onClear={() => { setFile(null); clearPreview(); }} />
          </div>
          <p className="font-sans text-[11px] text-[#8A8170] mt-4">Works best with 2–8 full outfits clearly visible. MOIRÉ rates the styling, never the people.</p>
        </div>
      )}

      {step === "analyzing" && (
        <AnalyzingSequence stages={["Finding the crew…", "Reading each look…", "Lining everyone up…"]} />
      )}

      {step === "evaluating" && (
        <AnalyzingSequence stages={["Reading the colour story…", "Checking the group cohesion…", "Scoring the lineup…"]} />
      )}

      {step === "confirm" && detected && (
        <div>
          <div className="text-center">
            <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">Group Check</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#F5F2EA] mt-2" data-testid="group-sees">
              MOIRÉ sees {detected.looks.length} {detected.looks.length === 1 ? "look" : "looks"}</h2>
          </div>

          {detected.too_large && (
            <p className="font-serif italic text-center text-[#E5C293] mt-4">That's a whole squad. For the best rating, keep around 2–8 full outfits clearly visible.</p>
          )}
          {!detected.too_large && detected.some_obscured && (
            <p className="font-serif italic text-center text-[#8A8170] mt-4" data-testid="group-obscured">
              I can read {detected.analyzable_count} of the {detected.people_count} looks clearly — a couple are partly blocked. You can retake or continue.</p>
          )}

          <div className="divide-y divide-[#F5F2EA]/8 border-y border-[#F5F2EA]/8 mt-6" data-testid="group-look-list">
            {detected.looks.map((l) => (
              <div key={l.label} className="flex items-center gap-3 py-3.5">
                <span className="font-serif text-[#C8A97E] text-lg w-14 shrink-0">{l.label.replace("LOOK ", "")}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-lg text-[#F5F2EA] leading-tight truncate">{lookSummary(l)}</div>
                  <div className="font-sans text-[9px] tracking-luxe uppercase text-[#8A8170] mt-0.5">{l.position}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  {l.dominant_colors.slice(0, 4).map((c, i) => (
                    <span key={i} className="h-5 w-5 rounded-full border border-[#F5F2EA]/15" style={{ backgroundColor: c.hex }} title={c.name} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mb-2">What's the occasion? <span className="normal-case tracking-normal">(optional)</span></p>
            <Select value={occasion || "none"} onValueChange={(v) => setOccasion(v === "none" ? null : v)}>
              <SelectTrigger className="bg-[#111111] border-[#F5F2EA]/15 text-[#F5F2EA]" data-testid="group-occasion"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0D0D0D] border-[#F5F2EA]/15 text-[#F5F2EA]">
                <SelectItem value="none">Just rate the look</SelectItem>
                {OCCASIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap gap-3 mt-7 justify-center">
            <button onClick={evaluate} data-testid="check-the-group" className="flex items-center gap-2 bg-[#C8A97E] text-[#0D0D0D] px-7 py-3 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] transition-colors"><Users size={15} strokeWidth={1.5} /> Check The Group</button>
            <button onClick={retake} data-testid="group-adjust" className="flex items-center gap-2 border border-[#F5F2EA]/15 px-6 py-3 font-sans text-xs tracking-luxe uppercase text-[#F5F2EA]/80 hover:border-[#C8A97E] transition-colors"><RotateCcw size={14} /> Adjust</button>
          </div>
        </div>
      )}

      {step === "result" && result && (
        <div>
          <div className="text-center relative">
            <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">MOIRÉ Group Look</p>
            <div className="font-serif text-6xl text-[#C8A97E]" data-testid="group-score">{Number(result.group_score).toFixed(1)}<span className="text-2xl text-[#8A8170]"> / 10</span></div>
            {verdict && (
              <motion.div initial={{ opacity: 0, y: 10, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="mt-2 relative inline-block">
                {verdict.tier === "exceptional" && (
                  <>
                    <motion.span initial={{ opacity: 0, scale: 0 }} animate={{ opacity: [0, 1, 0.7], scale: [0, 1.15, 1] }} transition={{ duration: 1.1, delay: 0.25 }} className="absolute -top-3 -right-6"><Sparkles size={22} className="text-[#E5C293]" strokeWidth={1.4} /></motion.span>
                    <motion.span initial={{ opacity: 0, scale: 0 }} animate={{ opacity: [0, 1, 0.6], scale: [0, 1, 0.9] }} transition={{ duration: 1.1, delay: 0.45 }} className="absolute -bottom-2 -left-5"><Sparkles size={14} className="text-[#C8A97E]" strokeWidth={1.4} /></motion.span>
                  </>
                )}
                <h2 className={`font-serif tracking-tight ${verdict.tier === "exceptional" ? "text-5xl md:text-6xl verdict-shimmer" : "text-4xl text-[#C8A97E]"}`} data-testid="group-verdict">{verdict.signature || result.reaction || verdict.reaction}</h2>
              </motion.div>
            )}
            {verdict?.signature && <p className="font-serif italic text-2xl text-[#F5F2EA] mt-2" data-testid="group-reaction">{result.reaction || verdict.reaction}</p>}
            <p className="font-sans text-xs text-[#8A8170] mt-2">{result.occasion_fit}</p>
            <div className="mt-6">
              <button onClick={() => setShareOpen(true)} data-testid="share-our-look"
                className={`inline-flex items-center gap-2 font-sans text-xs tracking-luxe uppercase transition-colors ${verdict?.tier === "exceptional" ? "bg-[#C8A97E] text-[#0D0D0D] px-8 py-3.5 hover:bg-[#E5C293]" : "border border-[#C8A97E]/60 text-[#C8A97E] px-6 py-3 hover:bg-[#C8A97E] hover:text-[#0D0D0D]"}`}>
                <Share2 size={15} strokeWidth={1.5} /> Share Our Look</button>
            </div>
          </div>

          {/* what's working */}
          <div className="mt-10">
            <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] mb-3">What's working</p>
            <div className="space-y-4" data-testid="group-working">
              {result.what_working.map((w, i) => (
                <div key={i}>
                  <p className="font-sans text-[9px] tracking-luxe uppercase text-[#8A8170]">{w.title}</p>
                  <p className="font-serif text-lg text-[#F5F2EA]/90 leading-snug mt-0.5">{w.note}</p>
                </div>
              ))}
            </div>
          </div>

          {/* group fix */}
          {result.group_fix && (
            <div className="mt-8 border border-[#F5F2EA]/10 p-5" data-testid="group-fix">
              <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] mb-2">If I'd change one thing…</p>
              <p className="font-serif text-xl text-[#F5F2EA]">{result.group_fix.title}</p>
              <p className="font-serif italic text-base text-[#F5F2EA]/80 mt-1">{result.group_fix.reason}</p>
              {result.group_fix.suggestions?.length > 0 && (
                <div className="mt-3">
                  <p className="font-sans text-[9px] tracking-luxe uppercase text-[#8A8170] mb-2">Try instead</p>
                  <div className="flex flex-wrap gap-2">
                    {result.group_fix.suggestions.map((s, i) => (
                      <span key={i} className="flex items-center gap-2 border border-[#F5F2EA]/12 px-3 py-1.5">
                        <span className="h-4 w-4 rounded-full border border-[#F5F2EA]/15" style={{ backgroundColor: s.color_hex }} />
                        <span className="font-sans text-[11px] text-[#F5F2EA]/85">{s.name}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* palette */}
          {result.palette?.length > 0 && (
            <div className="mt-8" data-testid="group-palette">
              <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] mb-3">Group palette</p>
              <div className="flex flex-wrap gap-3">
                {result.palette.map((c, i) => (
                  <div key={i} className="text-center">
                    <span className="block h-12 w-12 rounded-full border border-[#F5F2EA]/15" style={{ backgroundColor: c.hex }} />
                    <span className="block font-sans text-[9px] tracking-luxe uppercase text-[#8A8170] mt-1.5">{c.name}</span>
                  </div>
                ))}
              </div>
              <p className="font-serif italic text-base text-[#F5F2EA]/80 mt-3">{result.palette_note}</p>
            </div>
          )}

          {/* the lineup (secondary, no ranking) */}
          <div className="mt-8">
            <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] mb-3">The lineup</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="group-lineup">
              {result.lineup.map((l) => (
                <div key={l.label} className="border border-[#F5F2EA]/10 p-3 flex items-center justify-between">
                  <span className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">{l.label}</span>
                  <span className="font-serif text-2xl text-[#C8A97E]">{Number(l.score).toFixed(1)}</span>
                </div>
              ))}
            </div>
            <p className="font-sans text-[10px] text-[#8A8170] mt-2 italic">Individual outfit scores — for styling only, not a ranking of people.</p>
          </div>

          <div className="flex flex-wrap gap-3 mt-9 justify-center">
            {imageUrlRemote && <button onClick={deletePhoto} data-testid="group-delete-photo" className="flex items-center gap-2 border border-[#F5F2EA]/15 px-5 py-2.5 font-sans text-xs tracking-luxe uppercase text-[#8A8170] hover:text-[#F5F2EA] transition-colors"><X size={13} /> Delete Photo</button>}
            <button onClick={retake} className="flex items-center gap-2 border border-[#F5F2EA]/15 px-5 py-2.5 font-sans text-xs tracking-luxe uppercase text-[#F5F2EA]/70 hover:border-[#C8A97E] transition-colors"><RotateCcw size={13} /> New Group Check</button>
          </div>

          {/* share sheet */}
          <AnimatePresence>
            {shareOpen && (
              <div className="fixed inset-0 z-50 bg-[#0D0D0D]/85 flex items-end md:items-center justify-center p-0 md:p-5 overflow-y-auto" onClick={() => setShareOpen(false)}>
                <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} className="bg-[#0D0D0D] border border-[#C8A97E]/40 w-full md:max-w-md p-6 rounded-t-2xl md:rounded-none max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="group-share-sheet">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-serif text-2xl text-[#F5F2EA]">Share your group card</h3>
                    <button onClick={() => setShareOpen(false)} data-testid="group-share-close"><X size={18} className="text-[#8A8170]" /></button>
                  </div>
                  <div className="flex justify-center">
                    <GroupScoreCard innerRef={cardRef} score={result.group_score} verdict={verdict} photo={includePhoto ? (preview || (imageUrlRemote ? imageUrl(imageUrlRemote) : null)) : null} />
                  </div>
                  {(preview || imageUrlRemote) && (
                    <div className="grid grid-cols-2 gap-2 mt-5" data-testid="group-share-photo-toggle">
                      <button onClick={() => setIncludePhoto(false)} data-testid="group-share-card-only" className={`font-sans text-[10px] tracking-luxe uppercase px-3 py-2.5 border transition-colors ${!includePhoto ? "border-[#C8A97E] text-[#C8A97E]" : "border-[#F5F2EA]/12 text-[#8A8170] hover:border-[#C8A97E]/50"}`}>Score card only</button>
                      <button onClick={() => setIncludePhoto(true)} data-testid="group-share-with-photo" className={`flex items-center justify-center gap-1.5 font-sans text-[10px] tracking-luxe uppercase px-3 py-2.5 border transition-colors ${includePhoto ? "border-[#C8A97E] text-[#C8A97E]" : "border-[#F5F2EA]/12 text-[#8A8170] hover:border-[#C8A97E]/50"}`}><ImageIcon size={12} /> With our photo</button>
                    </div>
                  )}
                  <p className="font-sans text-[10px] text-[#8A8170] mt-3 text-center leading-relaxed">Group photos stay private by default. Only what you see here is shared — the crew's photo is only added if you choose it.</p>
                  <div className="flex gap-2.5 mt-5">
                    <button onClick={doShare} data-testid="group-share-send" className="flex-1 flex items-center justify-center gap-2 bg-[#C8A97E] text-[#0D0D0D] px-5 py-3 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] transition-colors"><Share2 size={14} strokeWidth={1.5} /> Share</button>
                    <button onClick={downloadCard} data-testid="group-share-download" className="flex items-center justify-center gap-2 border border-[#F5F2EA]/15 px-5 py-3 font-sans text-xs tracking-luxe uppercase text-[#F5F2EA]/80 hover:border-[#C8A97E] transition-colors"><Download size={14} strokeWidth={1.5} /> Save</button>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

function GroupScoreCard({ innerRef, score, verdict, photo }) {
  const signature = verdict?.signature;
  const reaction = verdict?.reaction;
  return (
    <div ref={innerRef} data-testid="group-score-card" style={{ width: 340, background: "#0D0D0D", padding: 26, fontFamily: "Cormorant Garamond, serif", border: "1px solid rgba(200,169,126,.28)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", marginBottom: 16 }}>
        <MoireSymbol size={20} />
        <span style={{ color: "#F5F2EA", letterSpacing: 6, fontSize: 14, fontFamily: "Manrope, sans-serif" }}>MOIRÉ</span>
      </div>
      {photo && (
        <div style={{ height: 240, marginBottom: 16, overflow: "hidden", border: "1px solid rgba(245,242,234,.1)" }}>
          <img src={photo} alt="group" crossOrigin="anonymous" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
      )}
      <div style={{ textAlign: "center" }}>
        {signature && <div style={{ color: "#C8A97E", fontSize: 40, letterSpacing: 1, lineHeight: 1.05 }}>{signature}</div>}
        <div style={{ color: "#8A8170", fontSize: 11, letterSpacing: 3, fontFamily: "Manrope, sans-serif", marginTop: signature ? 8 : 0 }}>GROUP LOOK</div>
        <div style={{ color: "#C8A97E", fontSize: 54, lineHeight: 1, marginTop: 4 }}>{Number(score).toFixed(1)}<span style={{ color: "#8A8170", fontSize: 20 }}> / 10</span></div>
        {reaction && <div style={{ color: "#F5F2EA", fontSize: 19, letterSpacing: 2, marginTop: 12, fontFamily: "Manrope, sans-serif", textTransform: "uppercase" }}>{reaction}</div>}
      </div>
      <div style={{ marginTop: 18, borderTop: "1px solid rgba(245,242,234,.12)", paddingTop: 10, display: "flex", justifyContent: "space-between", fontFamily: "Manrope, sans-serif" }}>
        <span style={{ color: "#8A8170", fontSize: 9, letterSpacing: 2 }}>DRESS UP. DON'T MESS UP.</span>
        <span style={{ color: "#8A8170", fontSize: 9, letterSpacing: 2 }}>MOIRE.LIVE</span>
      </div>
    </div>
  );
}
